import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { platformBrandingApi } from "../lib/api";

// Global product branding controlled by the super-admin Platform Console.
// Powers the tenant sidebar logo/name and the "Powered by ServiceGuru Pro"
// label shown on estimates / invoices and the public pay/accept pages.
const DEFAULTS = {
  sidebarLogoUrl: "/brand-logo.png",
  brandName: "ServiceGuru Pro",
  tagline: "Field Ops",
  poweredByLabel: "Powered by ServiceGuru Pro",
  poweredByUrl: "https://servicegurupro.com",
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Resolve a stored logo path to a loadable URL.
// - data:/http(s): used as-is
// - /api/static/...: served by the backend
// - /brand-logo.png (or other): a frontend public asset
export const resolveBrandAsset = (url) => {
  if (!url) return null;
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  if (url.startsWith("/api/")) return `${BACKEND}${url}`;
  return url;
};

const PlatformBrandingContext = createContext({ branding: DEFAULTS, refresh: () => {} });

export const usePlatformBranding = () => useContext(PlatformBrandingContext);

// Point the browser tab favicon + iOS home-screen icon at a given URL. Applied
// site-wide (this provider wraps every route, public + app) so the platform
// owner's uploaded logo becomes the web-app icon everywhere.
function applyFavicon(href) {
  if (typeof document === "undefined" || !href) return;
  const lower = href.split("?")[0].toLowerCase();
  const type = lower.endsWith(".svg")
    ? "image/svg+xml"
    : lower.endsWith(".jpg") || lower.endsWith(".jpeg")
    ? "image/jpeg"
    : lower.endsWith(".webp")
    ? "image/webp"
    : "image/png";
  const upsert = (rel) => {
    let link = document.querySelector(`link[rel='${rel}']`);
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", rel);
      document.head.appendChild(link);
    }
    link.setAttribute("type", type);
    link.removeAttribute("sizes");
    link.setAttribute("href", href);
  };
  upsert("icon");
  upsert("shortcut icon");
  upsert("apple-touch-icon");
}

// Swap the PWA manifest for the backend-generated one so the installable
// (Android/Chrome "Add to Home Screen") app icon uses the uploaded logo too.
function applyManifest() {
  if (typeof document === "undefined") return;
  let link = document.querySelector("link[rel='manifest']");
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "manifest");
    document.head.appendChild(link);
  }
  link.setAttribute("href", `${BACKEND}/api/platform-manifest.json`);
}

export function PlatformBrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULTS);

  const refresh = useCallback(async () => {
    try {
      const d = await platformBrandingApi.getPublic();
      setBranding({ ...DEFAULTS, ...(d || {}) });
    } catch {
      /* keep defaults on failure */
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      await Promise.resolve();
      if (active) refresh();
    })();
    return () => { active = false; };
  }, [refresh]);

  // Sync the browser/app icon with the platform-uploaded logo. Only override the
  // built-in favicon when a custom logo has actually been uploaded.
  useEffect(() => {
    const raw = branding.sidebarLogoUrl;
    if (!raw || raw === DEFAULTS.sidebarLogoUrl) return;
    const href = resolveBrandAsset(raw);
    if (href) applyFavicon(href);
    applyManifest();
  }, [branding.sidebarLogoUrl]);

  return (
    <PlatformBrandingContext.Provider value={{ branding, refresh }}>
      {children}
    </PlatformBrandingContext.Provider>
  );
}
